import React, { useMemo, useState } from "react";
import { NavLink as RouterNavLink, useLocation } from "react-router-dom";
import { DecryptToken } from "../helper/Constants";
import {
  Ban,
  Building2,
  ChevronDown,
  Circle,
  Hash,
  History,
  PhoneCall,
  Users,
  Volume2,
  Waves,
} from "lucide-react";

const Sidebar = ({ openNav, navHandler }) => {
  const [expandedMenus, setExpandedMenus] = useState({});
  const { pathname } = useLocation();
  const [userType] = useState(() => {
    const userToken = localStorage.getItem("psx_token");
    const decodedToken = DecryptToken(userToken);
    return decodedToken?.user_type ?? null;
  });

  const handleNavClick = () => {
    if (window.innerWidth < 1024 && typeof navHandler === "function") {
      navHandler();
    }
  };

  const toggleMenu = (menuId) => {
    setExpandedMenus((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  const SidebarLink = ({ to, label, icon: Icon, isSubLink = false }) => {
    return (
      <RouterNavLink
        to={to}
        end
        className={({ isActive }) =>
          `mx-3 my-1 flex items-center gap-3 rounded-xl px-4 py-2 transition-all duration-200 group ${
            isActive
              ? "bg-gradient-to-r from-sky-600 to-cyan-600 text-white font-semibold shadow-sm dark:from-sky-500 dark:to-cyan-500"
              : "text-slate-600 dark:text-slate-300 hover:bg-sky-50 dark:hover:bg-sky-950/30 hover:text-sky-800 dark:hover:text-sky-200"
          }`
        }
        onClick={handleNavClick}
      >
        {({ isActive }) => (
          <>
            {Icon && (
              <Icon
                size={isSubLink ? 14 : 18}
                className={
                  isActive
                    ? "text-current"
                    : "text-slate-400 group-hover:text-sky-700 dark:group-hover:text-sky-300"
                }
              />
            )}
            <span className={`${isSubLink ? "text-sm" : "text-[15px]"} flex-1`}>
              {label}
            </span>
          </>
        )}
      </RouterNavLink>
    );
  };

  const SubNavMenu = ({ title, links, icon: Icon }) => {
    const menuId = title.toLowerCase().replace(/\s+/g, "-");
    const hasActiveChild = links.some((link) => pathname === link.to);
    const isExpanded = expandedMenus[menuId] ?? hasActiveChild;

    return (
      <li className="mb-1">
        <div
          className={`mx-3 flex cursor-pointer items-center gap-3 rounded-lg px-4 py-2.5 transition-all duration-200 group ${
            isExpanded || hasActiveChild
              ? "text-sky-800 dark:text-sky-200"
              : "text-slate-600 dark:text-slate-300"
          } hover:bg-sky-50 dark:hover:bg-sky-950/30`}
          onClick={() => toggleMenu(menuId)}
        >
          {Icon && (
            <Icon
              size={18}
              className="text-slate-400 group-hover:text-sky-700 dark:group-hover:text-sky-300"
            />
          )}
          <span className="flex-1 font-medium text-[15px]">{title}</span>
          <ChevronDown
            size={16}
            className={`opacity-50 transition-transform duration-300 ${
              isExpanded ? "rotate-180" : ""
            }`}
          />
        </div>
        <ul
          className={`overflow-hidden transition-all duration-300 ease-in-out ${
            isExpanded ? "mt-1 max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}
        >
          {links.map((link, index) => (
            <li key={index} className="pl-6">
              <SidebarLink
                to={link.to}
                label={link.label}
                icon={link.icon}
                isSubLink={true}
              />
            </li>
          ))}
        </ul>
      </li>
    );
  };

  const userType3Navigation = useMemo(
    () => [
      {
        to: "/calls",
        label: "Calls Overview",
        icon: Building2,
        allowedUserTypes: [3],
      },
      {
        to: "/manage-users",
        label: "Manage Extensions",
        icon: Users,
        allowedUserTypes: [3],
      },
      {
        to: "/ring-group",
        label: "Queues",
        icon: Waves,
        allowedUserTypes: [3],
      },
      {
        to: "/greetings",
        label: "Greetings",
        icon: Volume2,
        allowedUserTypes: [3],
      },
      {
        to: "/purchase-number-list",
        label: "DID Numbers",
        icon: Hash,
        allowedUserTypes: [3],
      },
      {
        to: "/block-number",
        label: "Blocked Numbers",
        icon: Ban,
        allowedUserTypes: [3],
      },
      {
        to: "/call-history",
        label: "Call History",
        icon: History,
        allowedUserTypes: [3],
      },
      {
        to: "/extension-live-calls",
        label: "Live Calls",
        icon: PhoneCall,
        allowedUserTypes: [3],
      },
    ],
    []
  );

  const defaultNavigationItems = useMemo(
    () => [
      {
        to: "/dashboard",
        label: "Dashboard",
        icon: Building2,
        allowedUserTypes: [1, 2, 4, 5],
      },
      {
        title: "Numbers",
        icon: Hash,
        allowedUserTypes: [1, 2, 4, 5],
        links: [
          { to: "/purchase-number-list", label: "DID Numbers", icon: Circle },
          { to: "/block-number", label: "Blocked Numbers", icon: Circle },
        ],
      },
      {
        title: "Phone System",
        icon: PhoneCall,
        allowedUserTypes: [1, 2, 4, 5],
        links: [
          { to: "/calls", label: "Calls Overview", icon: Circle },
          { to: "/manage-users", label: "Manage Extensions", icon: Circle },
          { to: "/ring-group", label: "Queues & Ring Groups", icon: Circle },
          { to: "/greetings", label: "Greetings", icon: Circle },
          { to: "/call-history", label: "Call History", icon: Circle },
          { to: "/report-list", label: "Reports", icon: Circle },
        ],
      },
    ],
    []
  );

  const navigationItems = useMemo(
    () => (userType === 3 ? userType3Navigation : defaultNavigationItems),
    [defaultNavigationItems, userType, userType3Navigation]
  );

  const filteredNavItems = useMemo(() => {
    return navigationItems.filter((item) => {
      if (!userType) {
        return true;
      }

      return item.allowedUserTypes.includes(userType);
    });
  }, [navigationItems, userType]);

  return (
    <aside
      className={`admin-sidebar-shell fixed top-0 left-0 z-40 h-screen w-64 overflow-y-auto transition-transform duration-300 custom-scrollbar ${
        openNav ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      }`}
      style={{ paddingTop: "64px" }}
    >
      <nav className="pb-6">
        <ul className="space-y-1">
          {filteredNavItems.map((item, index) => (
            <React.Fragment key={index}>
              {item.title ? (
                <SubNavMenu title={item.title} links={item.links} icon={item.icon} />
              ) : (
                <li>
                  <SidebarLink to={item.to} label={item.label} icon={item.icon} />
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;
